import { BackdropSpinner } from '@ps-smart-cp/ppfw-client';
import { type ComponentType, type LazyExoticComponent, lazy, type ReactElement, Suspense } from 'react';
import {
  LOADING_INDICATOR_DELAY_MS,
  NAV_STALL_EVENT,
  NAV_STALL_THRESHOLD_MS,
  type NavStallDetail,
  notifyIfNotSettledWithin,
  resolveWithin,
  retryOnChunkLoadError,
} from './services/lazyPageService';

interface PageModule {
  Component: ComponentType;
}

interface LazyPageProps {
  component: LazyExoticComponent<ComponentType>;
}

function LazyPage({ component: LazyComponent }: LazyPageProps) {
  return (
    <Suspense fallback={<BackdropSpinner />}>
      <LazyComponent />
    </Suspense>
  );
}

/**
 * route の `lazy` に渡す関数を作る。
 *
 * react-router は `lazy` が解決するまで URL も画面も変えず、何も表示しない。
 * chunk の取得が遅いと、リンクを押しても反応が無いように見えるため、
 * {@link LOADING_INDICATOR_DELAY_MS} を超えたら `lazy` を先に解決して遷移を確定させ、
 * 遷移先の URL で読み込み中の表示を出す。
 *
 * Suspense は route ごとに置く。react-router は遷移を startTransition で包むため、
 * すでに表示されている Suspense では fallback が出ず、前の画面が残るからである。
 *
 * 読み込みが最後まで失敗した場合は、描画中に例外が投げられ、route の errorElement が受ける。
 */
function lazyPage(factory: () => Promise<PageModule>) {
  return async (): Promise<PageModule | { element: ReactElement }> => {
    const fromPathname = window.location.pathname;
    const startedAt = performance.now();
    const modulePromise = retryOnChunkLoadError(factory);

    notifyIfNotSettledWithin(modulePromise, NAV_STALL_THRESHOLD_MS, () => {
      const detail: NavStallDetail = {
        fromPathname,
        waitedMs: Math.round(performance.now() - startedAt),
      };
      window.dispatchEvent(new CustomEvent(NAV_STALL_EVENT, { detail }));
    });

    const loaded = await resolveWithin(modulePromise, LOADING_INDICATOR_DELAY_MS);
    if (loaded) {
      return loaded;
    }

    const LazyComponent = lazy(async () => ({ default: (await modulePromise).Component }));
    return { element: <LazyPage component={LazyComponent} /> };
  };
}

export { lazyPage };
